import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Briefcase, MapPin, Send, Sparkles } from "lucide-react";
import { OPEN_ROLES } from "@/lib/site-data";
import SectionHeader from "@/components/SectionHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Careers() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", role: "", experience_years: 0, skills: "", cover_note: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const v = e.target.name === "experience_years" ? parseInt(e.target.value || "0", 10) : e.target.value;
    setForm({ ...form, [e.target.name]: v });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.role || !form.skills) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/careers`, form);
      toast.success("Application received! Our HR will reach out soon.");
      setForm({ full_name: "", email: "", phone: "", role: "", experience_years: 0, skills: "", cover_note: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="careers-page">
      <section className="relative bg-white py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 grid-bg pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-700 mb-4">
              Careers · Join Us
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 leading-[1.02]">
              Build a career engineering <span className="text-red-600">enterprise uptime.</span>
            </h1>
            <p className="mt-7 text-lg text-slate-600 leading-relaxed max-w-2xl">
              We're hiring engineers, field specialists and consultants across India. If you
              love networking, virtualization or Microsoft platforms — we want to talk.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-2xl overflow-hidden border border-slate-200">
              <img
                src="https://images.unsplash.com/photo-1599548199555-7994a155bf54?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxJVCUyMHN1cHBvcnQlMjBlbmdpbmVlciUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NzgwODYxNjR8MA&ixlib=rb-4.1.0&q=85"
                alt="IT professional"
                className="w-full h-[420px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Open Roles */}
      <section className="bg-slate-50 py-20 lg:py-24" data-testid="open-roles-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeader eyebrow="Open positions" title="Roles we're hiring for." testid="roles-header" />
          <div className="mt-12 grid md:grid-cols-2 gap-4">
            {OPEN_ROLES.map((r, i) => (
              <div
                key={r.title}
                data-testid={`role-${i}`}
                className="bg-white border border-slate-200 rounded-xl p-6 flex items-start justify-between gap-4 hover:border-blue-300 transition-colors"
              >
                <div className="flex gap-4">
                  <div className="h-11 w-11 rounded-lg bg-blue-50 text-blue-900 flex items-center justify-center flex-shrink-0">
                    <Briefcase className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold text-slate-900">{r.title}</h3>
                    <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{r.location}</span>
                      <span className="h-1 w-1 rounded-full bg-slate-300" />
                      <span>{r.type}</span>
                    </div>
                  </div>
                </div>
                <a
                  href="#apply"
                  data-testid={`role-apply-${i}`}
                  className="text-sm font-semibold text-blue-900 whitespace-nowrap hover:text-red-600 transition-colors"
                >
                  Apply →
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Apply Form */}
      <section id="apply" className="bg-white py-20 lg:py-28" data-testid="apply-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <Sparkles className="h-8 w-8 text-red-600 mx-auto mb-4" />
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-900">
              Apply for a role
            </h2>
            <p className="mt-4 text-slate-600">Tell us about yourself — we'll be in touch.</p>
          </div>
          <form
            onSubmit={handleSubmit}
            data-testid="career-form"
            className="bg-slate-50 border border-slate-200 rounded-2xl p-8 lg:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full Name *" name="full_name" value={form.full_name} onChange={handleChange} placeholder="Your name" />
              <Field label="Email *" name="email" type="email" value={form.email} onChange={handleChange} placeholder="you@email.com" />
              <Field label="Phone *" name="phone" value={form.phone} onChange={handleChange} placeholder="+91 9XXXX XXXXX" />
              <Field label="Role applying for *" name="role" value={form.role} onChange={handleChange} placeholder="e.g., Network Engineer" />
              <Field label="Years of experience" name="experience_years" type="number" value={form.experience_years} onChange={handleChange} placeholder="0" />
              <Field label="Key skills *" name="skills" value={form.skills} onChange={handleChange} placeholder="VMware, Cisco, Windows Server..." />
            </div>
            <div className="mt-5">
              <label className="block text-xs font-bold uppercase tracking-[0.18em] text-slate-700 mb-2">
                Cover note <span className="text-slate-400 font-normal normal-case tracking-normal">(optional)</span>
              </label>
              <textarea
                name="cover_note"
                value={form.cover_note}
                onChange={handleChange}
                rows={5}
                data-testid="career-input-cover_note"
                placeholder="Why R-Tech? Anything you'd like us to know..."
                className="w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="career-submit-button"
              className="mt-7 inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white px-7 py-3.5 rounded-md font-semibold transition-all hover:-translate-y-0.5"
            >
              {submitting ? "Submitting..." : (<>Submit Application <Send className="h-4 w-4" /></>)}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, placeholder, type = "text" }) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-[0.18em] text-slate-700 mb-2">
        {label}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={`career-input-${name}`}
        className="w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
      />
    </div>
  );
}
